import React from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'
import { MapHouse, TooltipArrowWhite } from '@/components/icons'
import { FlyToBounds } from '@/components/map/fly-to-bounds'
import Map from '@/components/maplibre-map/map'
import Marker from '@/components/maplibre-map/marker'
import { ParagraphSmall } from '@/components/text'

const getLocation = (data: { lat: string; lon: string }): [number, number] => [
  Number(data.lat),
  Number(data.lon)
]

const MapContainer = styled.div`
  width: 100%;
  height: 255px;
  margin-top: 8px;
  z-index: 0;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 8px 16px 0;
    width: unset;
    height: 270px;
  }

  .leaflet-container {
    border-radius: 16px;
  }

  .leaflet-control-zoom {
    display: none;
  }
`

const MarkerContainer = styled.div`
  position: absolute;
  border-radius: 50%;
  top: -42px;
  left: -19px;
  background-color: rgba(19, 43, 62, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 92px;
`

const MarkerWrapper = styled.div`
  background-color: ${colors.blue16};
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;

  > svg {
    color: ${colors.white};
    width: 24px;
    height: 24px;
  }
`

const PermanentTooltipContainer = styled.div`
  position: absolute;
  bottom: 52px;
  left: 50%;
  transform: translateX(-50%);
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 275px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 220px;
  }
`

const TooltipTextArea = styled(ParagraphSmall)`
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 12px;
  background: ${colors.white};
  border-radius: 8px;
  color: ${colors.black};
  box-shadow: 0px 4px 6px -2px rgba(0, 0, 0, 0.05),
    0px 10px 15px -3px rgba(0, 0, 0, 0.1);
`

const StyledTooltipArrow = styled(TooltipArrowWhite)`
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  color: ${colors.white};
`

interface MarkerComponentProps {
  popupText: string
}

const MarkerComponent: React.FC<MarkerComponentProps> = ({ popupText }) => {
  return (
    <MarkerContainer>
      <MarkerWrapper>
        <PermanentTooltipContainer>
          <TooltipTextArea>
            {popupText}
            <StyledTooltipArrow />
          </TooltipTextArea>
        </PermanentTooltipContainer>
        <MapHouse />
      </MarkerWrapper>
    </MarkerContainer>
  )
}

const MapComponent = ({ building }: any): any => {
  const { t } = useTranslation()
  const hasLatLon = building?.lat && building?.lon
  const buildingObj = hasLatLon
    ? building
    : {
        lat: '48.6886900',
        lon: '-123.1330200'
      }

  return (
    <MapContainer>
      <Map
        center={getLocation(buildingObj)}
        zoom={5}
        navigationControl={{ enabled: false }}
      >
        {hasLatLon && (
          <FlyToBounds
            centerPoint={{
              lat: buildingObj.lat,
              lon: buildingObj.lon
            }}
          />
        )}
        <Marker
          position={getLocation(buildingObj)}
          IconComponent={() => (
            <MarkerComponent
              popupText={t(
                'l.listing.view.location_tooltip.text',
                'Your location will be displayed by default, but you can choose to hide it if you prefer.'
              )}
            />
          )}
        />
      </Map>
    </MapContainer>
  )
}

export default MapComponent
